import { insertInventoryTransactionSchema, type InventoryTransaction } from "../platform/schema";

export const TRANSACTION_AMEND_FIELDS = [
  "supplierContactId",
  "purchaserContactId",
  "notes",
  "price",
  "referenceNumber",
  "supplierBatchNumber",
  "transactionDate",
] as const;

export type TransactionAmendField = typeof TRANSACTION_AMEND_FIELDS[number];

export const HISTORY_TRACKED_TRANSACTION_FIELDS = [
  "chemicalId", "transactionType", "inventoryApplied", "quantity", "finalCountedAmount", "unit", "transactionDate",
  "supplierId", "supplierContactId", "purchaserId", "purchaserContactId", "price", "referenceNumber",
  "supplierBatchNumber", "usageDetails", "disposalReason", "notes",
] as const satisfies readonly (keyof InventoryTransaction)[];

type FieldOwner = "transaction" | "inventory";
type AmendMode = "editable" | "read-only" | "correction-only";
export type TransactionAmendSchemaKind = "contactId" | "string" | "moneyString" | "dateTimeString";

type TransactionFieldPolicy = {
  owner: FieldOwner;
  appliedAmendMode: AmendMode;
  recordOnlyAmendMode: AmendMode;
  schemaKind: TransactionAmendSchemaKind;
};

export type TransactionMetadataPatch = Partial<Pick<InventoryTransaction, TransactionAmendField | "transactionType">>;

export const TRANSACTION_FIELD_POLICY = {
  supplierContactId: {
    owner: "transaction",
    appliedAmendMode: "editable",
    recordOnlyAmendMode: "editable",
    schemaKind: "contactId",
  },
  purchaserContactId: {
    owner: "transaction",
    appliedAmendMode: "editable",
    recordOnlyAmendMode: "editable",
    schemaKind: "contactId",
  },
  notes: {
    owner: "transaction",
    appliedAmendMode: "editable",
    recordOnlyAmendMode: "editable",
    schemaKind: "string",
  },
  price: {
    owner: "transaction",
    appliedAmendMode: "editable",
    recordOnlyAmendMode: "editable",
    schemaKind: "moneyString",
  },
  referenceNumber: {
    owner: "transaction",
    appliedAmendMode: "editable",
    recordOnlyAmendMode: "editable",
    schemaKind: "string",
  },
  supplierBatchNumber: {
    owner: "transaction",
    appliedAmendMode: "editable",
    recordOnlyAmendMode: "editable",
    schemaKind: "string",
  },
  transactionDate: {
    owner: "transaction",
    appliedAmendMode: "correction-only",
    recordOnlyAmendMode: "editable",
    schemaKind: "dateTimeString",
  },
} as const satisfies Record<TransactionAmendField, TransactionFieldPolicy>;

export function isTransactionAmendFieldEditable(
  field: TransactionAmendField,
  inventoryApplied: boolean,
) {
  const mode = inventoryApplied
    ? TRANSACTION_FIELD_POLICY[field].appliedAmendMode
    : TRANSACTION_FIELD_POLICY[field].recordOnlyAmendMode;
  return mode === "editable";
}

export function getAmendableTransactionFields(inventoryApplied: boolean) {
  return TRANSACTION_AMEND_FIELDS.filter((field) =>
    isTransactionAmendFieldEditable(field, inventoryApplied)
  );
}

export function amendmentFieldsFor(inventoryApplied: boolean, reclassifying: boolean): TransactionAmendField[] {
  const fields: TransactionAmendField[] = getAmendableTransactionFields(inventoryApplied);
  return reclassifying ? Array.from(new Set<TransactionAmendField>([...fields, "transactionDate"])) : fields;
}

export const transactionMetadataInputSchema = insertInventoryTransactionSchema
  .pick({ [insertInventoryTransactionSchema.keyof().enum.price]: true }).passthrough();
